import { useState } from "react"

import { useMutation, useQueryClient } from "@tanstack/react-query"

import { supabase } from "@/lib/supabase"

interface IssueUniformParams {
  driverId: string
  uniformItem: string
  quantity?: number
  givenBy: string
  dateGiven?: string
}

interface IssueUniformResponse {
  success: boolean
  data: Record<string, unknown>
}

interface UseIssueUniformResult {
  issueUniform: (params: IssueUniformParams) => Promise<IssueUniformResponse>
  isLoading: boolean
  error: Error | null
  data: IssueUniformResponse | null
}

export function useIssueUniform(): UseIssueUniformResult {
  const [error, setError] = useState<Error | null>(null)
  const [data, setData] = useState<IssueUniformResponse | null>(null)
  const queryClient = useQueryClient()

  const mutation = useMutation<IssueUniformResponse, Error, IssueUniformParams>({
    mutationFn: async (params: IssueUniformParams): Promise<IssueUniformResponse> => {
      // Ensure required fields are present
      if (!params.driverId) {
        throw new Error("Driver ID is required")
      }
      if (!params.uniformItem) {
        throw new Error("Uniform item is required")
      }
      if (!params.givenBy) {
        throw new Error("Given by is required")
      }

      // Set default values if not provided
      const requestData = {
        driverId: params.driverId,
        uniformItem: params.uniformItem,
        quantity: params.quantity || 1,
        givenBy: params.givenBy,
        dateGiven: params.dateGiven || new Date().toISOString().split("T")[0],
      }

      const { data, error } = await supabase.functions.invoke("issue-uniform", {
        body: requestData,
      })

      if (error) {
        throw new Error(`Error issuing uniform item: ${error.message}`)
      }

      return data as IssueUniformResponse
    },
    onSuccess: () => {
      // Invalidate and refetch uniform data queries
      queryClient.invalidateQueries({ queryKey: ["uniform"] })
    },
    onError: (err: Error) => {
      setError(err)
    },
    onSettled: (responseData) => {
      if (responseData) {
        setData(responseData)
      }
    },
  })

  const issueUniform = async (params: IssueUniformParams): Promise<IssueUniformResponse> => {
    return mutation.mutateAsync(params)
  }

  return {
    issueUniform,
    isLoading: mutation.isPending,
    error,
    data,
  }
}
